import { useState, useCallback } from 'react'
import { Search, Trash2, Upload, X, Image as ImageIcon, CheckSquare, Square, Loader2, AlertCircle } from 'lucide-react'
import { useDropzone } from 'react-dropzone'
import { useToast } from '@/components/ui/Toast'

const API_BASE = '/api'
const PRESTASHOP_BASE = 'https://www.jvs-informatica.com'

interface ProductImage {
  id_image: number
  position: number
  cover: boolean
  url: string
}

interface ProductWithImages {
  id_product: number
  reference: string
  name: string
  active: boolean
  image_count: number
  images: ProductImage[]
}

export default function ProductImagesPage() {
  const toast = useToast()

  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [products, setProducts] = useState<ProductWithImages[]>([])

  // Selection state
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set())

  // Upload state
  const [filesToUpload, setFilesToUpload] = useState<File[]>([])
  const [replaceAll, setReplaceAll] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Search products
  const handleSearch = async () => {
    if (!searchQuery.trim()) return

    setIsSearching(true)
    try {
      const response = await fetch(`${API_BASE}/product-images/search?query=${encodeURIComponent(searchQuery)}`)
      if (!response.ok) throw new Error('Error en la búsqueda')

      const data = await response.json()
      setProducts(data.products)
      // Auto-select all by default
      setSelectedProductIds(new Set(data.products.map((p: ProductWithImages) => p.id_product)))
    } catch (error) {
      toast.error('Error al buscar productos')
      console.error(error)
    } finally {
      setIsSearching(false)
    }
  }

  // Toggle product selection
  const toggleProductSelection = (productId: number) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev)
      if (next.has(productId)) {
        next.delete(productId)
      } else {
        next.add(productId)
      }
      return next
    })
  }

  // Select/deselect all
  const toggleSelectAll = () => {
    if (selectedProductIds.size === products.length) {
      setSelectedProductIds(new Set())
    } else {
      setSelectedProductIds(new Set(products.map(p => p.id_product)))
    }
  }

  // Delete single image
  const handleDeleteImage = async (imageId: number) => {
    if (!confirm('¿Eliminar esta imagen?')) return

    try {
      const response = await fetch(`${API_BASE}/product-images/image/${imageId}`, {
        method: 'DELETE'
      })
      if (!response.ok) throw new Error('Error al eliminar')

      toast.success('Imagen eliminada')
      // Refresh search results
      handleSearch()
    } catch (error) {
      toast.error('Error al eliminar imagen')
      console.error(error)
    }
  }

  // Delete all images from selected products
  const handleDeleteAllImages = async () => {
    if (selectedProductIds.size === 0) {
      toast.error('Selecciona al menos un producto')
      return
    }

    if (!confirm(`¿Eliminar TODAS las imágenes de ${selectedProductIds.size} producto(s)?`)) return

    setIsDeleting(true)
    try {
      const response = await fetch(`${API_BASE}/product-images/bulk/images`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: Array.from(selectedProductIds) })
      })

      const data = await response.json()
      if (data.success) {
        toast.success(`${data.deleted_images} imágenes eliminadas`)
      } else {
        toast.error(`Eliminación parcial: ${data.errors.length} errores`)
      }

      // Refresh
      handleSearch()
    } catch (error) {
      toast.error('Error al eliminar imágenes')
      console.error(error)
    } finally {
      setIsDeleting(false)
    }
  }

  // Handle file drop
  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFilesToUpload(prev => [...prev, ...acceptedFiles])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'] },
    multiple: true
  })

  // Remove file from upload list
  const removeFile = (index: number) => {
    setFilesToUpload(prev => prev.filter((_, i) => i !== index))
  }

  // Upload images to selected products
  const handleUpload = async () => {
    if (selectedProductIds.size === 0) {
      toast.error('Selecciona al menos un producto')
      return
    }

    if (filesToUpload.length === 0) {
      toast.error('Añade al menos una imagen')
      return
    }

    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append('product_ids', JSON.stringify(Array.from(selectedProductIds)))
      formData.append('replace_all', String(replaceAll))
      filesToUpload.forEach(file => {
        formData.append('images', file)
      })

      const response = await fetch(`${API_BASE}/product-images/bulk/images`, {
        method: 'POST',
        body: formData
      })

      const data = await response.json()
      if (data.success) {
        toast.success(`${data.uploaded_count} imágenes subidas a ${data.products_updated} productos`)
        setFilesToUpload([])
      } else {
        toast.error(`Subida parcial: ${data.errors.length} errores`)
      }

      // Refresh
      handleSearch()
    } catch (error) {
      toast.error('Error al subir imágenes')
      console.error(error)
    } finally {
      setIsUploading(false)
    }
  }

  // Get selected products
  const selectedProducts = products.filter(p => selectedProductIds.has(p.id_product))

  // Get all images from selected products
  const selectedImages = selectedProducts.flatMap(p =>
    p.images.map(img => ({ ...img, productId: p.id_product, productRef: p.reference }))
  )

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <ImageIcon className="w-6 h-6" />
        Gestionar Imágenes de Productos
      </h1>

      {/* Search Section */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-4 mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="ID de producto o referencia (ej: 319564 o PANT-INT-027)"
            className="flex-1 px-4 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <button
            onClick={handleSearch}
            disabled={isSearching}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Buscar
          </button>
        </div>
      </div>

      {/* Results Table */}
      {products.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow mb-6">
          <div className="p-4 border-b dark:border-slate-700 flex justify-between items-center">
            <h2 className="font-semibold">
              {products.length} producto(s) encontrado(s) - {selectedProductIds.size} seleccionado(s)
            </h2>
            <button
              onClick={toggleSelectAll}
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              {selectedProductIds.size === products.length ? (
                <>
                  <CheckSquare className="w-4 h-4" />
                  Deseleccionar todos
                </>
              ) : (
                <>
                  <Square className="w-4 h-4" />
                  Seleccionar todos
                </>
              )}
            </button>
          </div>

          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-700">
              <tr>
                <th className="px-4 py-2 text-left w-12"></th>
                <th className="px-4 py-2 text-left">ID</th>
                <th className="px-4 py-2 text-left">Referencia</th>
                <th className="px-4 py-2 text-left">Nombre</th>
                <th className="px-4 py-2 text-center">Imágenes</th>
                <th className="px-4 py-2 text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr
                  key={product.id_product}
                  className={`border-t dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 ${selectedProductIds.has(product.id_product) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  onClick={() => toggleProductSelection(product.id_product)}
                >
                  <td className="px-4 py-2">
                    {selectedProductIds.has(product.id_product) ? (
                      <CheckSquare className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Square className="w-5 h-5 text-slate-400" />
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-sm">{product.id_product}</td>
                  <td className="px-4 py-2 font-mono text-sm">{product.reference}</td>
                  <td className="px-4 py-2 text-sm truncate max-w-xs">{product.name}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={`px-2 py-1 rounded text-sm ${product.image_count > 0 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                      {product.image_count}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className={`px-2 py-1 rounded text-xs ${product.active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'
                      }`}>
                      {product.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Gallery of selected products */}
      {selectedImages.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow mb-6 p-4">
          <h2 className="font-semibold mb-4">
            Imágenes actuales ({selectedImages.length})
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {selectedImages.map((img, idx) => (
              <div key={`${img.productId}-${img.id_image}`} className="relative group">
                <img
                  src={`${PRESTASHOP_BASE}${img.url}`}
                  alt={`Producto ${img.productRef}`}
                  className="w-full h-32 object-cover rounded-lg border dark:border-slate-600"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%2394a3b8" font-size="12">Sin imagen</text></svg>'
                  }}
                />
                <div className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                  {img.productRef}
                </div>
                {img.cover && (
                  <div className="absolute top-1 right-1 bg-yellow-500 text-white text-xs px-1.5 py-0.5 rounded">
                    Cover
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteImage(img.id_image)
                  }}
                  className="absolute bottom-1 right-1 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Eliminar imagen"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Section */}
      {products.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-4">
          <h2 className="font-semibold mb-4">Subir nuevas imágenes</h2>

          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragActive
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-slate-300 dark:border-slate-600 hover:border-blue-400'
              }`}
          >
            <input {...getInputProps()} />
            <Upload className="w-10 h-10 mx-auto mb-2 text-slate-400" />
            {isDragActive ? (
              <p className="text-blue-600">Suelta las imágenes aquí...</p>
            ) : (
              <p className="text-slate-500">
                Arrastra imágenes aquí o haz clic para seleccionar
              </p>
            )}
          </div>

          {/* File preview */}
          {filesToUpload.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium mb-2">
                {filesToUpload.length} imagen(es) para subir:
              </h3>
              <div className="flex flex-wrap gap-2">
                {filesToUpload.map((file, idx) => (
                  <div key={idx} className="relative">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="w-20 h-20 object-cover rounded border"
                    />
                    <button
                      onClick={() => removeFile(idx)}
                      className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    {idx === 0 && (
                      <div className="absolute bottom-0 left-0 right-0 bg-yellow-500 text-white text-xs text-center py-0.5">
                        Cover
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Options */}
          <div className="mt-4 flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={replaceAll}
                onChange={e => setReplaceAll(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Eliminar imágenes existentes antes de subir</span>
            </label>
          </div>

          {/* Actions */}
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleUpload}
              disabled={isUploading || filesToUpload.length === 0 || selectedProductIds.size === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Subir a {selectedProductIds.size} producto(s)
            </button>

            <button
              onClick={handleDeleteAllImages}
              disabled={isDeleting || selectedProductIds.size === 0}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Eliminar todas las imágenes
            </button>
          </div>

          {/* Help text */}
          {selectedProductIds.size > 0 && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <p className="font-medium">Las imágenes se subirán a {selectedProductIds.size} producto(s):</p>
                <p className="text-xs mt-1">
                  {selectedProducts.map(p => p.reference).join(', ')}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {products.length === 0 && !isSearching && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-8 text-center">
          <ImageIcon className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h2 className="text-lg font-medium mb-2">Busca un producto para gestionar sus imágenes</h2>
          <p className="text-slate-500">
            Introduce un ID de producto o una referencia para ver y modificar sus imágenes.
          </p>
        </div>
      )}
    </div>
  )
}
